import React, { createRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
    CamerasIcon,
    ChevronIcon,
    SelectedIcon,
    EffectsIcon,
    EnvironmentsIcon,
    LightsIcon,
    MaterialsIcon,
    ObjectsIcon,
    TexturesIcon,
    TriggerIcon,
    WorldIcon
} from '../../../../widgets/icons/scene';
import { T } from '../../../../../shared/helpers';
import { useSubscriptionData } from '../../../../../shared/hooks/useSubscriptionData';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { useProductConfigurationState } from '../../state';
import HeaderToolbar from '../configurator-components/headerToolbar';
import LeftVerticalToolbar, { StepsTools } from '../configurator-components/leftVerticalToolbar';
import CameraEditor from '../configurator-components/scene-editors/cameraEditor';
import LightsEditor from '../configurator-components/scene-editors/lightsEditor';
import MaterialsEditor from '../configurator-components/scene-editors/materialsEditor';
import { materialsWithoutPreview, shootPreviews } from '../configurator-components/scene-editors/materialPreview';
import ObjectEditor from '../configurator-components/scene-editors/objectEditor';
import TriggerEditor from '../configurator-components/scene-editors/triggerEditor';
import { VTOProviderSync } from '../configurator-components/scene-editors/vtoEditor';
import TexturesEditor from '../configurator-components/scene-editors/texturesEditor';
import EnvironmentsEditor from '../configurator-components/scene-editors/environmentsEditor';
import WorldEditor from '../configurator-components/scene-editors/worldEditor';
import EffectsEditor from '../configurator-components/scene-editors/effectsEditor';
import { TreeHandle } from '../configurator-components/TreeComponent';
import SceneEditorSettingsDialog from '../configurator-components/sceneEditorSettingsDialog';
import SceneEditorLibraryDrawer from './sceneEditorLibraryDrawer';
import { SectionPlacementProvider } from '../configurator-components/scene-editors/sectionPanel';
import { InspectorChromeProvider } from '../configurator-components/scene-editors/inspectorShell';
import { useMovableHost } from './useMovableHost';

// Relay generated types
import useSaveProductAndScene from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import OverlayLoading from '../../../../widgets/overlayLoading';
import { Load3DSceneEditor } from '../../../../widgets/preview3D';
import SceneEditorView from '../../../../widgets/preview3D/sceneEditorView';
import { SceneEditorToolbar } from '../configurator-components/scene-editors/toolbar';
import { AssetBar, Panel, Resizer, StageColumn, Workspace } from '../../../../widgets/workspaceLayout/workspaceLayout';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { getWorkspacePanelMinimum, getWorkspaceRailWidth, useWorkspacePreferences, WORKSPACE_MAX_PANEL_SIZE } from './useWorkspacePreferences';

interface MeshTreeViewItem {
    id: string;
    name: string;
}

enum Mode {
    Meshes = 'meshes',
    Materials = 'materials',
    Lights = 'lights',
    Camera = 'camera'
}

interface SceneEditorPageState {
    isEnvironmentTextureUploading: boolean;
    isSkyboxTextureUploading: boolean;
    zkModel: any;
    zkScene: any;
    meshes: any[];
    materials: any[];
    lights: any[];
    cameraLocations: any[];
    tasks: any[];
    selectedMeshes: any[];
    selectedMaterials: any[];
    selectedLights: any[];
    selectedCameraLocations: any[];
    filters: {
        meshes: string;
        lights: string;
        materials: string;
    };
    mode: Mode;
    propertiesPanelOpen: boolean;
    loadingPercentage: number;
    texturesLoading: any[];
    showPreview: boolean;
    explodeMode: boolean;
    hasVTOReferenceModels: boolean;
    selectedVTOType: any;
    tryOnSettingsComponent: React.RefObject<any>;
    isAdmin: boolean;
    importDropdownMenuIsOpened: boolean;
    isUploadingDamMaterial: boolean;
    providersMapping: {
        list: Record<string, any>;
    };
    uploadedQueue: any[];
    groupMaterialsDisplayOrder: Map<string, number>;
}

const Container = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    z-index: 30;
    width: 100%;

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const PreviewContainer = styled(Panel)<{ $previewMode?: boolean }>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    min-height: 0;
    padding: 20px 12px 34px;
    border: 0;
    border-radius: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.borderRadius};

    ${(props) =>
        props.$previewMode &&
        `
            & > *:not([data-preview-mode-control]) {
                visibility: hidden;
            }
        `}
`;

const InspectorPanel = styled(Panel)<{ $collapsed?: boolean }>`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    align-items: ${(props) => (props.$collapsed ? 'center' : 'stretch')};
`;

const InspectorToggleButton = styled.button<{ $rotate?: number }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    color: ${(props) => props.theme.app.textColor};
    background: transparent;
    cursor: pointer;

    ${(props) => props.$rotate && `svg { transform: rotate(${props.$rotate}deg); }`}

    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.app.secondary};
        background: ${(props) => props.theme.app.pageBackgroundColor};
        outline: none;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

// IL RAIL DELL'INSPECTOR — solo nel secondo attracco. Tre voci per la stessa domanda, «cosa
// sto guardando»: la cosa scelta, il mondo intorno, gli effetti sopra. Nel primo attracco non
// esiste: lì World ed Effects sono voci del rail di sinistra, insieme alle liste.
const InspectorRail = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 6px 8px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const InspectorRailItem = styled.button<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    height: 26px;
    padding: 0 8px;
    border: 0;
    border-radius: 6px;
    color: ${(props) => (props.$active ? props.theme.app.secondary : props.theme.app.primary)};
    background: ${(props) => (props.$active ? props.theme.app.pageBackgroundColor : 'transparent')};
    font-size: 12px;
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
    }

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }

    &:hover:not(:disabled),
    &:focus-visible {
        color: ${(props) => props.theme.app.secondary};
        outline: none;
    }
`;

// Il posto in cui la sezione d'ambiente viene traslocata quando l'Inspector la mostra.
const InspectorSectionSlot = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
`;

// Lo scorrimento non sta qui: ce l'ha il corpo di `InspectorShell`, sotto la sua testata, che
// deve restare ferma mentre le proprietà scorrono.
const InspectorContent = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow: hidden;
`;

// IL POSTO DICE CHE È VUOTO invece di sparire. Stessa forma del mockup: un glifo, una riga di
// prosa, tutto attenuato — non è un errore e non è un comando: è la colonna che aspetta un
// soggetto.
const InspectorEmpty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px 28px;
    text-align: center;
    color: ${(props) => props.theme.app.primary};

    p {
        margin: 0;
        font-size: 12px;
        line-height: 1.6;
    }

    svg {
        width: 26px;
        height: 26px;
        opacity: 0.5;
    }
`;

const SmallScreenBanner = styled.div`
    display: none;

    @media (max-width: 940px) {
        display: block;
        position: absolute;
        top: 12px;
        left: 12px;
        right: 12px;
        z-index: 20;
    }
`;

const StageEmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.app.primary};
`;

const SceneEditor: React.FC = () => {
    const { showDialog, closeDialog } = useDialogManager();
    // Hook per ottenere i dati della subscription con additionalFeatures
    const { subscription } = useSubscriptionData();
    const additionalFeatures = subscription?.plan?.additionalFeatures;

    // Refs per mantenere le referenze come nella classe
    // UN RIFERIMENTO PER SEZIONE, e non uno solo per tutte.
    //
    // «Portami a quella cosa» ha un bersaglio diverso a seconda di dove si va: una mesh
    // nell'albero degli oggetti, un materiale nella sua libreria. Con un riferimento unico vince
    // l'ultima sezione che si è montata — e siccome la libreria vive in un portale che sta in piedi
    // anche quando non la si guarda, vinceva lei: selezionare una mesh nel palco chiedeva a un
    // materiale di aprirsi, non lo trovava, e non apriva niente.
    //
    // Chi chiama dice già a quale sezione va (`revealTarget(tool, id)`): basta che il riferimento
    // lo scelga in base a quello.
    const treeRef = useRef<TreeHandle>(null);
    const materialsRef = useRef<TreeHandle>(null);
    const meshesScrollbarsRef = useRef<any>(null);
    const materialsScrollbarsRef = useRef<any>(null);
    const workspaceRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const { preferences, setPreference, setPanelSize, toggleCollapsed, persist } = useWorkspacePreferences();
    const [inspectorContent, setInspectorContent] = useState<React.ReactNode | null>(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(true);

    // Hook per il salvataggio del prodotto e scena
    const [saveProductAndScene, isSaving] = useSaveProductAndScene();

    // Variabili di istanza convertite in useRef per persistenza
    const lastTasksLengthRef = useRef<number>(0);
    const treeItemsRef = useRef<MeshTreeViewItem[]>([]);
    const creatingGroupParentRef = useRef<any | null>(null);
    const sidebarWidthRef = useRef<number>(0);
    const sceneReadyRef = useRef<boolean>(false);
    const currentTaskRef = useRef<any | null>(null);

    // Stato iniziale convertito da this.state
    const [state, setState] = useState<SceneEditorPageState>({
        isEnvironmentTextureUploading: false,
        isSkyboxTextureUploading: false,
        zkModel: null,
        zkScene: undefined,
        meshes: [],
        materials: [],
        lights: [],
        cameraLocations: [],
        tasks: [],
        selectedMeshes: [],
        selectedMaterials: [],
        selectedLights: [],
        selectedCameraLocations: [],
        filters: { meshes: '', lights: '', materials: '' },
        mode: Mode.Meshes,
        propertiesPanelOpen: false,
        loadingPercentage: 0,
        texturesLoading: [],
        showPreview: false,
        explodeMode: false,
        hasVTOReferenceModels: false,
        selectedVTOType: undefined,
        tryOnSettingsComponent: createRef(),
        isAdmin: localStorage.getItem('is_admin') ? true : false,
        importDropdownMenuIsOpened: false,
        isUploadingDamMaterial: false,
        providersMapping: { list: {} },
        uploadedQueue: [],
        groupMaterialsDisplayOrder: new Map<string, number>()
    });

    // Stato per overlay loading (convertito da proprietà di classe)
    const { dispatch, state: productState, unsavedChanges } = useProductConfigurationState();
    const [zkProduct, zkScene, sceneEditor, activeTool, setActiveTool] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneEditor,
        x.sceneEditorActiveTool,
        x.setSceneEditorActiveTool
    ]);
    const [isSceneEditorReady, setIsSceneEditorReady] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const markSceneDirty = useCallback(() => {
        if (sceneReadyRef.current) setHasUnsavedChanges(true);
    }, []);

    const activeSceneEditorTool = activeTool ?? T._('Objects', 'Admin');

    useEffect(() => {
        if (!activeTool) setActiveTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, activeTool, setActiveTool]);

    // Ref per la funzione di refresh della visibilità delle mesh
    const refreshMeshVisibilityRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (sceneEditor && zkScene) {
            sceneReadyRef.current = false;
            const meshes = sceneEditor.getSceneMeshes();
            const materials = sceneEditor.getAllMaterials();
            const lights = sceneEditor.getAllLights();

            // AGGIUNTO: Carica le camera locations dal zkScene
            const cameraLocations = zkScene.get('cameraLocations')?.models || [];

            handleMeshesUpdated(meshes);
            handleMaterialsUpdated(materials);
            handleCameraLocationsUpdated(cameraLocations);
            setHasUnsavedChanges(false);
            sceneReadyRef.current = true;
            setIsSceneEditorReady(true);

            // Refresh mesh visibility dopo il caricamento iniziale
            setTimeout(() => {
                if (refreshMeshVisibilityRef.current) {
                    refreshMeshVisibilityRef.current();
                }
            }, 100);
        }
        return () => {
            sceneReadyRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneEditor, zkScene]);

    // Event handlers stabilizzati con useCallback
    const handleMeshesUpdated = useCallback(
        (meshes: any[]) => {
            markSceneDirty();

            setState((prevState) => ({
                ...prevState,
                meshes: (meshes ?? [])
                    .filter((mesh) => {
                        return mesh.name.toLowerCase() !== 'material_mesh';
                    })
                    .sort((a: any, b: any) => {
                        if (a.name < b.name) return -1;
                        if (a.name > b.name) return 1;
                        return 0;
                    })
            }));
        },
        [markSceneDirty]
    );

    const handleMaterialsUpdated = useCallback(
        (materials: any[]) => {
            markSceneDirty();
            // Il problema: a volte riceviamo direttamente i materiali, altre volte un gruppo che li contiene
            let actualMaterials = materials;

            // Se il primo elemento ha una proprietà 'materials', allora è un gruppo
            if (materials && materials.length > 0 && materials[0] && materials[0].materials) {
                actualMaterials = materials[0].materials;
            }

            const filteredMaterials = (actualMaterials ?? [])
                .filter((material) => {
                    return material.name && material.name.toLowerCase() !== 'default_material';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

            setState((prevState) => ({
                ...prevState,
                materials: filteredMaterials
            }));
        },
        [markSceneDirty]
    );

    // Aggiorna handleLightsUpdated per essere più completo
    const handleLightsUpdated = useCallback(
        (lights: any[]) => {
            markSceneDirty();
            const filteredLights = (lights ?? [])
                .filter((light) => {
                    // Filtra eventuali luci di sistema o default se necessario
                    return light.name && light.name.toLowerCase() !== 'default_light';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

            setState((prevState) => ({
                ...prevState,
                lights: filteredLights
            }));
        },
        [markSceneDirty]
    );

    const handleCameraLocationsUpdated = useCallback(
        (cameraLocations: any[]) => {
            markSceneDirty();
            setState((s) => ({ ...s, cameraLocations }));
        },
        [markSceneDirty]
    );

    // Callback per registrare la funzione di refresh delle mesh
    const handleRefreshMeshVisibility = useCallback((refreshFn: () => void) => {
        refreshMeshVisibilityRef.current = refreshFn;
    }, []);

    const handleSceneLoaded = useCallback((scene: BABYLON.Scene) => {
        void scene;
    }, []);

    const handleBeforeSceneLoaded = useCallback(async () => {
        return;
    }, []);

    const handleSceneTasksCheck = useCallback((...args: any[]) => {
        // If the first argument is the tasks array, update state accordingly
        const tasks = Array.isArray(args[0]) ? args[0] : args;
        setState((s) => ({ ...s, tasks }));
    }, []);

    const handleMeshUpdated = useCallback(
        (updatedMesh: any) => {
            markSceneDirty();
            setState((prevState) => ({
                ...prevState,
                meshes: prevState.meshes.map((mesh) => (mesh.uniqueId === updatedMesh.uniqueId ? updatedMesh : mesh))
            }));
        },
        [markSceneDirty]
    );

    const notifySceneEditorUpdates = useCallback(() => {
        if (!sceneEditor) return;

        sceneEditor.render();
        sceneEditor.updateBoundingBox();
    }, [sceneEditor]);

    // Funzione per il salvataggio usando l'hook
    const handleSave = useCallback(async () => {
        try {
            const productInfo = {
                name: productState.product.name,
                code: productState.product.code,
                environment: 2
            };

            // ✅ NUOVO: Validazione AR PRIMA della notificazione di saving
            if (sceneEditor) {
                const { validateARBeforeSave } = await import('src/components/widgets/preview3D/hooks/useSaveProduct');
                await validateARBeforeSave(sceneEditor);
            }

            // Se il VTO è attivo, gestisci le impostazioni VTO prima del salvataggio
            if (sceneEditor && zkScene) {
                // Always hide all references on save so they are not visible and not
                // downloaded at start of customizer/DAM
                sceneEditor.setVTOReferenceVisible(false);

                // Verifica se il VTO è abilitato e applica le impostazioni
                const tryOnSettings = sceneEditor.getTryOnSettings();
                if (tryOnSettings && zkScene.getTryOnEnabled()) {
                    // Applica le impostazioni VTO correnti alla scena
                    sceneEditor.setTryOnTypeSettings(tryOnSettings);
                }

                // Imposta il nome della scena basato sul nome del prodotto
                zkScene.set('name', productInfo.name + ' Scene');
            }

            // Mostra notifica di progresso
            const notificationId = showInfoNotification({
                message: T._('Saving scene...', 'Admin')
            });

            // AL SALVATAGGIO SI RIEMPIONO I BUCHI. Un materiale senza fotografia resta un tondo
            // disegnato in libreria, e chi lo guarda non ha modo di sapere che basta chiederla: qui
            // si è già fermi ad aspettare, ed è il momento in cui uno scatto costa meno.
            //
            // SOLO QUELLE CHE MANCANO. Rifarle tutte vorrebbe dire minuti di scatti a ogni
            // salvataggio, e le altre sono già giuste. Le fotografie entrano nei metadata della
            // scena, quindi vanno prese PRIMA che la scena parta: dopo, sarebbero salvate la volta
            // dopo.
            const missingPreviews = materialsWithoutPreview(sceneEditor, state.materials);
            if (missingPreviews.length) await shootPreviews(sceneEditor, missingPreviews);

            await saveProductAndScene({
                onProgress: (progress) => {
                    void progress;
                },
                fromSceneEditor: true,
                // ✅ NUOVO: Passa i dati corretti del prodotto
                productInfo: productInfo
            });

            // Rimuovi notifica di progresso e mostra successo
            removeNotification(notificationId);
            setHasUnsavedChanges(false);
            unsavedChanges.resetSnapshot();
            showSuccessNotification({
                message: T._('Scene saved successfully', 'Admin')
            });
            return true;
        } catch (error) {
            console.error('Errore durante il salvataggio:', error);

            // Mostra notifica di errore
            showErrorNotification({
                message: T._('Failed to save the scene', 'Admin')
            });
            return false;
        }
    }, [saveProductAndScene, sceneEditor, zkScene, productState.product, state.materials, unsavedChanges]);

    // LE DUE LISTE SI SEGUONO. Ritrovare una cosa vuol dire due movimenti insieme: la colonna di
    // sinistra cambia sezione, e dentro quella sezione la riga giusta si accende — coi gruppi
    // chiusi aperti lungo la strada. Prima il primo movimento mangiava il secondo: cliccando una
    // mesh nel palco mentre eri sui materiali, la sezione passava agli oggetti e lì finiva, con
    // l'elenco aperto sulla riga sbagliata. Bisognava cliccare la stessa mesh una seconda volta.
    //
    // IL PANNELLO NUOVO NON C'È ANCORA quando si chiede il cambio: si monta in questo giro di
    // render, e il suo albero esiste dal giro dopo. Si dichiara quindi cosa si stava cercando, e
    // appena la sezione è quella giusta la riga si accende da sé.
    const [pendingReveal, setPendingReveal] = useState<{ tool: string; dataID: string } | null>(null);

    const refOfTool = useCallback((tool: string) => (tool === T._('Materials', 'Admin') ? materialsRef : treeRef), []);

    const revealTarget = useCallback(
        (tool: string, dataID: string) => {
            if (activeSceneEditorTool !== tool) {
                setActiveTool(tool);

                // NON SI SVUOTA NELL'ATTESA. Il soggetto nuovo arriva un giro dopo, quando l'albero
                // della sezione si è montato: svuotare adesso fa chiudere e riaprire la colonna per una
                // cosa che sta per essere sostituita.
                setPendingReveal({ tool, dataID });
                return;
            }

            refOfTool(tool).current?.selectAndOpenOverlay?.(dataID);
        },
        [activeSceneEditorTool, setActiveTool, refOfTool]
    );

    useEffect(() => {
        if (!pendingReveal || activeSceneEditorTool !== pendingReveal.tool) return;

        const { dataID, tool } = pendingReveal;
        setPendingReveal(null);
        // Un giro dopo: l'albero della sezione nuova si sta montando proprio adesso.
        const frame = requestAnimationFrame(() => refOfTool(tool).current?.selectAndOpenOverlay?.(dataID));
        return () => cancelAnimationFrame(frame);
    }, [pendingReveal, activeSceneEditorTool, refOfTool]);

    const handleMeshSelected = useCallback((meshID: any) => revealTarget(T._('Objects', 'Admin'), meshID), [revealTarget]);

    // «DOVE STA?» NON È «PORTAMI LÀ», e finora erano la stessa funzione. Cliccare una mesh nel palco
    // vuol dire «adesso guardo questa», e sceglierla è la risposta giusta. La freccetta dentro uno
    // Slot chiede un'altra cosa: QUALE sia e DOVE stia — e rispondere scegliendola butta via il
    // soggetto che si stava leggendo, aprendo il pannello del materiale proprio a chi stava
    // guardando l'oggetto che lo usa.
    //
    // La domanda quindi cambia sezione e ACCENDE la riga, e basta: è la stessa risposta che l'albero
    // dà già alla freccetta della sua testata.
    const [whereRequest, setWhereRequest] = useState<{ tool: string; dataID: string; token: number } | null>(null);

    const askWhereItIs = useCallback(
        (tool: string, dataID: string) => {
            if (activeSceneEditorTool !== tool) setActiveTool(tool);

            // IL GETTONE SERVE A CHIEDERE DUE VOLTE LA STESSA RIGA: senza, la seconda domanda sarebbe
            // lo stesso valore di prima e non succederebbe niente.
            setWhereRequest((previous) => ({ tool, dataID, token: (previous?.token ?? 0) + 1 }));
        },
        [activeSceneEditorTool, setActiveTool]
    );

    // I DUE «PORTAMI LÀ» SONO FUNZIONI STABILI, e non è pignoleria: scritti in linea dentro il JSX
    // nascono nuovi a ogni ridisegno di questa pagina, e una sezione che li mette fra le dipendenze
    // di una `useCallback` si ritrova a rifare tutto — la fabbrica dei suoi pannelli, il suo albero
    // di nodi — a ogni giro. Costa poco scriverli qui e vale per chiunque li riceva: è l'invariante
    // che rende innocuo dipenderne.
    const revealMaterial = useCallback((materialID: string) => askWhereItIs(T._('Materials', 'Admin'), materialID), [askWhereItIs]);
    const revealObject = useCallback((meshID: string) => askWhereItIs(T._('Objects', 'Admin'), meshID), [askWhereItIs]);

    // I DATI VIVI PASSANO DA UN RIFERIMENTO, e non dalla chiusura delle fabbriche qui sotto: è la
    // condizione che permette a quelle di avere identità fissa. Si riempie durante il disegno,
    // subito prima che le sezioni lo leggano.
    const live = useRef<any>({});

    live.current = {
        state,
        sceneEditor,
        zkScene,
        additionalFeatures,
        handleMeshUpdated,
        notifySceneEditorUpdates,
        handleMeshesUpdated,
        handleMaterialsUpdated,
        handleLightsUpdated,
        handleCameraLocationsUpdated,
        handleRefreshMeshVisibility,
        revealMaterial,
        revealObject,
        whereRequest
    };

    // LE FABBRICHE DELLE SEZIONI HANNO IDENTITÀ FISSA, e non è un'ottimizzazione. La barra a
    // sinistra rende `component` come TIPO di componente — `<ToolComponent/>` — e per React un tipo
    // diverso è un componente diverso: smonta quello di prima e ne monta uno nuovo da zero. Scritte
    // in linea dentro il memo qui sotto, queste funzioni rinascevano a ogni cambio di `state.lights`
    // o `state.materials`, cioè a ogni modifica della scena.
    //
    // COSA COSTAVA: la sezione aperta perdeva tutto — il suo elenco, la selezione, la riga aperta —
    // e siccome nessuno aveva più una riga aperta, nessuno rifaceva il pannello: la colonna di
    // destra restava a disegnare l'ultima cosa che le era stata consegnata. Da fuori era «rinomino
    // una luce e il pannello mostra il nome vecchio». Il cambio di specie non si riprendeva mai,
    // perché a morire era anche il ricordo della luce appena nata che serviva a riaprirla.
    const ObjectsTool = useCallback(
        (props: any) => (
            <ObjectEditor
                onMeshUpdate={live.current.handleMeshUpdated}
                sceneEditor={live.current.sceneEditor}
                notifySceneEditorUpdates={live.current.notifySceneEditorUpdates}
                onMeshesUpdated={live.current.handleMeshesUpdated}
                onRevealMaterial={live.current.revealMaterial}
                onRefreshMeshVisibility={live.current.handleRefreshMeshVisibility}
                whereRequest={live.current.whereRequest?.tool === T._('Objects', 'Admin') ? live.current.whereRequest : null}
                {...props}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const LightsTool = useCallback(
        (props: any) => (
            <LightsEditor
                {...props}
                lights={live.current.state.lights}
                selectedLights={live.current.state.selectedLights}
                sceneEditor={live.current.sceneEditor}
                onLightsUpdated={live.current.handleLightsUpdated}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const CamerasTool = useCallback(
        (props: any) => (
            <CameraEditor
                {...props}
                cameraLocations={live.current.state.cameraLocations}
                selectedCameraLocations={live.current.state.selectedCameraLocations}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                onCameraLocationsUpdated={live.current.handleCameraLocationsUpdated}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const TriggerTool = useCallback(() => <TriggerEditor />, []);

    const MaterialsTool = useCallback(
        (props: any) => (
            <MaterialsEditor
                {...props}
                materials={live.current.state.materials}
                selectedMaterials={live.current.state.selectedMaterials}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                onMaterialsUpdated={live.current.handleMaterialsUpdated}
                onRevealObject={live.current.revealObject}
                whereRequest={live.current.whereRequest?.tool === T._('Materials', 'Admin') ? live.current.whereRequest : null}
                treeRef={materialsRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const TexturesTool = useCallback(() => <TexturesEditor />, []);

    const EnvironmentsTool = useCallback(() => <EnvironmentsEditor />, []);

    const WorldTool = useCallback(
        () => <WorldEditor sceneEditor={live.current.sceneEditor} zkScene={live.current.zkScene} additionalFeatures={live.current.additionalFeatures} />,
        []
    );

    const EffectsTool = useCallback(() => <EffectsEditor sceneEditor={live.current.sceneEditor} zkScene={live.current.zkScene} />, []);

    // NOVE VOCI IN TRE GENERI, come il prototipo. Sopra ciò che sta NELLA scena — oggetti,
    // luci, camere, trigger point; in mezzo la LIBRERIA da cui la scena pesca — materiali,
    // texture, ambienti; sotto ciò che sta INTORNO — il mondo e gli effetti. I separatori
    // sono i capitoli: senza, nove glifi in colonna sono un elenco senza indice.
    const sceneEditorTools: StepsTools[] = useMemo(
        () => [
            {
                tool: T._('Objects', 'Admin'),
                icon: <ObjectsIcon />,
                tooltip: T._('Objects', 'Admin'),
                onClick: () => {},
                component: ObjectsTool
            },
            {
                tool: T._('Lights', 'Admin'),
                icon: <LightsIcon />,
                tooltip: T._('Lights', 'Admin'),
                onClick: () => {},
                component: LightsTool
            },
            {
                tool: T._('Cameras', 'Admin'),
                icon: <CamerasIcon />,
                tooltip: T._('Cameras', 'Admin'),
                onClick: () => {},
                component: CamerasTool
            },
            {
                tool: T._('Trigger', 'Admin'),
                icon: <TriggerIcon />,
                tooltip: T._('Trigger points', 'Admin'),
                onClick: () => {},
                component: TriggerTool
            },
            {
                tool: T._('Materials', 'Admin'),
                icon: <MaterialsIcon />,
                tooltip: T._('Materials', 'Admin'),
                onClick: () => {},
                separatorBefore: true,
                component: MaterialsTool
            },
            {
                tool: T._('Textures', 'Admin'),
                icon: <TexturesIcon />,
                tooltip: T._('Textures', 'Admin'),
                onClick: () => {},
                component: TexturesTool
            },
            {
                tool: T._('Env', 'Admin'),
                icon: <EnvironmentsIcon />,
                tooltip: T._('Environments', 'Admin'),
                onClick: () => {},
                component: EnvironmentsTool
            },
            {
                tool: T._('World', 'Admin'),
                icon: <WorldIcon />,
                tooltip: T._('World', 'Admin'),
                onClick: () => {},
                separatorBefore: true,
                component: WorldTool
            },
            {
                tool: T._('Effects', 'Admin'),
                icon: <EffectsIcon />,
                tooltip: T._('Effects', 'Admin'),
                onClick: () => {},
                component: EffectsTool
            }
        ],
        [ObjectsTool, LightsTool, CamerasTool, TriggerTool, MaterialsTool, TexturesTool, EnvironmentsTool, WorldTool, EffectsTool]
    );

    // Chiudere il pannello lo chiude per tutte: non si sa da quale sezione era stato aperto, e una
    // sezione che resta convinta di avere un pannello aperto lo riapre al primo aggiornamento.
    const closeOverlay = useCallback(() => {
        setInspectorContent(null);
        treeRef.current?.closeOverlay();
        materialsRef.current?.closeOverlay();
    }, []);

    // Lo store del motore, non il suo contenuto: serve a riportarlo dentro le finestre, che si
    // disegnano fuori da questo albero di componenti.
    const preview3DStore = useContext(Preview3DStoreContext);

    const openSettings = useCallback(() => {
        // Le impostazioni si applicano mentre le tocchi — un tema lo si giudica vedendolo —
        // ma «Cancel» le rimette com'erano davvero: la revoca la scrive chi apre, che è
        // l'unico a sapere cosa c'era prima.
        const before = preferences;

        showDialog(
            'scene-editor-settings',
            // LA FINESTRA ESCE DAL PANNELLO, e con lui dal contesto del motore 3D: le finestre di questo
            // backoffice si disegnano in un portale attaccato alla radice della pagina, quindi tutto ciò
            // che sta dentro si ritrova FUORI dal provider che tiene scena, editor e prodotto. Il VTO lo
            // chiede al primo respiro e cadeva con «usePreview3DStore must be used within a
            // Preview3DStoreContext.Provider» — cioè la pagina intera andava in errore aprendo una
            // sezione delle impostazioni.
            //
            // Si riporta dentro lo stesso store, non una copia: è il gesto che il DAM e il caricamento
            // dei materiali fanno già quando aprono una finestra loro.
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <Dialog
                    title={T._('Settings', 'Admin')}
                    size='large'
                    onClose={() => {
                        persist(before);
                        closeDialog('scene-editor-settings');
                    }}
                    buttons={[
                        {
                            label: T._('Cancel', 'Admin'),
                            outline: true,
                            onClick: () => {
                                persist(before);
                                closeDialog('scene-editor-settings');
                            }
                        },
                        { label: T._('Confirm', 'Admin'), onClick: () => closeDialog('scene-editor-settings') }
                    ]}
                >
                    <SceneEditorSettingsDialog
                        preferences={preferences}
                        setPreference={setPreference}
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        zkProduct={zkProduct}
                        additionalFeatures={additionalFeatures}
                        onSceneChanged={markSceneDirty}
                    />
                </Dialog>
            </Preview3DStoreContext.Provider>
        );
    }, [additionalFeatures, closeDialog, markSceneDirty, persist, preferences, preview3DStore, sceneEditor, setPreference, showDialog, zkProduct, zkScene]);

    const leftMinimum = getWorkspacePanelMinimum('left', preferences.sectionRails);
    const rightMinimum = getWorkspacePanelMinimum('right', preferences.sectionRails);
    const assetsMinimum = getWorkspacePanelMinimum('assets', preferences.sectionRails);
    const showCentralLibrary = preferences.layoutMode === 'workspace' && preferences.propertiesPanel === 'right';
    // Con la striscia sotto il palco la libreria ha già il suo posto: tenere le sue tre
    // categorie anche nel rail vorrebbe dire due porte sulle stesse sezioni. Nell'altro
    // attracco la striscia non esiste, e tornano liste come le altre.
    const libraryToolNames = useMemo(() => [T._('Materials', 'Admin'), T._('Textures', 'Admin'), T._('Env', 'Admin')], []);
    // World ed Effects non parlano di una cosa scelta, parlano della scena: nel secondo
    // attracco sono voci del rail dell'**Inspector**, dove stanno accanto a «Selected» — cioè
    // accanto all'altra risposta alla stessa domanda, «cosa sto guardando».
    const ambientToolNames = useMemo(() => [T._('World', 'Admin'), T._('Effects', 'Admin')], []);
    const relocatedToolNames = useMemo(() => [...libraryToolNames, ...ambientToolNames], [libraryToolNames, ambientToolNames]);

    const railTools = useMemo(() => {
        if (!showCentralLibrary) return sceneEditorTools;
        return sceneEditorTools.filter((stepTool) => !relocatedToolNames.includes(stepTool.tool));
    }, [sceneEditorTools, showCentralLibrary, relocatedToolNames]);

    // LA SEZIONE DELLA LIBRERIA È UNA SOLA, E CAMBIA CASA. Nella colonna sta nell'Outliner,
    // nella striscia sta sotto il palco — ma è **lo stesso nodo**, non due copie: due
    // implementazioni sarebbero due stati da tenere d'accordo, ed è esattamente il difetto
    // che c'era (la striscia disegnava tessere finte e il layout 2 perdeva la sezione).
    //
    // In React spostare un componente da un genitore all'altro lo smonta e lo rimonta:
    // selezione, filtro e anteprime se ne andrebbero a ogni cambio d'attracco. Quindi il
    // nodo che lo contiene si crea **una volta**, la sezione ci vive dentro per portale, e a
    // spostarsi è il nodo — con `appendChild`, e **solo se il genitore è cambiato davvero**,
    // perché rimetterlo a ogni giro gli farebbe perdere il fuoco e azzererebbe lo
    // scorrimento (docs/scene-editor-step6-second-layout.md § 1).
    const [libraryTool, setLibraryTool] = useState(() => libraryToolNames[0]);
    const activeLibraryTool = libraryToolNames.includes(activeSceneEditorTool) ? activeSceneEditorTool : libraryTool;
    const librarySection = useMemo(() => sceneEditorTools.find((stepTool) => stepTool.tool === activeLibraryTool), [sceneEditorTools, activeLibraryTool]);

    useEffect(() => {
        if (libraryToolNames.includes(activeSceneEditorTool)) setLibraryTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, libraryToolNames]);

    // Le linguette della striscia sono le stesse tre voci del rail: stesso nome, stesso
    // glifo, stesso ordine. Il conto lo porta chi ce l'ha; le due che non hanno ancora
    // niente dietro restano dichiarate e spente, così il posto si vede prima di esistere.
    const libraryCategories = useMemo(
        () =>
            libraryToolNames.map((tool) => {
                const stepTool = sceneEditorTools.find((candidate) => candidate.tool === tool);
                const isMaterials = tool === libraryToolNames[0];
                return {
                    tool,
                    icon: stepTool?.icon,
                    count: isMaterials ? state.materials.length : 0,
                    disabled: !isMaterials
                };
            }),
        [libraryToolNames, sceneEditorTools, state.materials.length]
    );

    // SI RIDUCE A RAIL SOLO CHI UN RAIL CE L'HA. La colonna dell'Inspector si stringe in una
    // striscia perché quella striscia è il suo rail: nel secondo attracco ci restano dentro
    // «Selected», World ed Effects, cioè un modo di tornare. Nel primo attracco quel rail non
    // esiste, e una striscia con dentro solo una freccetta è spazio occupato per non dire
    // niente — lì l'Inspector se ne va del tutto, com'è giusto: la colonna è il soggetto, e
    // senza soggetto non c'è colonna.
    const isInspectorCollapsible = showCentralLibrary;

    // I comandi della colonna, non del soggetto: li prende il guscio dell'Inspector.
    const inspectorChrome = useMemo(
        () => ({ onCollapse: isInspectorCollapsible ? () => toggleCollapsed('right') : closeOverlay }),
        [closeOverlay, isInspectorCollapsible, toggleCollapsed]
    );

    // LA SEZIONE D'AMBIENTE, stessa storia della libreria: una sola, che cambia casa. Nel
    // primo attracco è una voce del rail come le altre, nel secondo è una voce del rail
    // dell'Inspector — e in mezzo non si rimonta.
    const [ambientTool, setAmbientTool] = useState(() => ambientToolNames[0]);
    const activeAmbientTool = ambientToolNames.includes(activeSceneEditorTool) ? activeSceneEditorTool : ambientTool;
    const ambientSection = useMemo(() => sceneEditorTools.find((stepTool) => stepTool.tool === activeAmbientTool), [sceneEditorTools, activeAmbientTool]);

    useEffect(() => {
        if (ambientToolNames.includes(activeSceneEditorTool)) setAmbientTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, ambientToolNames]);

    // Cosa mostra l'Inspector nel secondo attracco: la cosa scelta, oppure una delle due
    // sezioni d'ambiente. Nel primo attracco la domanda non si pone — lì l'Inspector ha un
    // soggetto solo e le due sezioni stanno nel rail di sinistra.
    const [inspectorTool, setInspectorTool] = useState<'selected' | 'ambient'>('selected');

    const railContentRef = useRef<HTMLDivElement | null>(null);
    const stripBodyRef = useRef<HTMLDivElement>(null);
    const inspectorSlotRef = useRef<HTMLDivElement | null>(null);
    // La presa del secondo pannello si misura sul pannello stesso, non sul palco: vedi `side` in
    // `workspaceLayout`.
    const inspectorRef = useRef<HTMLDivElement | null>(null);
    const { hostRef: libraryHostRef, moveTo: moveLibrary } = useMovableHost();
    const { hostRef: ambientHostRef, moveTo: moveAmbient } = useMovableHost();

    const railSelectedTool = railTools.some((stepTool) => stepTool.tool === activeSceneEditorTool) ? activeSceneEditorTool : railTools[0]?.tool;
    const isLibraryExpanded = showCentralLibrary && libraryOpen && !preferences.collapsed.includes('assets');

    // Il trasloco. Nella colonna il nodo entra nel contenitore del rail, ma solo quando la
    // sezione scelta è una della libreria: se no lì c'è un'altra sezione e i due si
    // affiancherebbero. Nella striscia entra nel corpo, anche da chiusa — è il posto vuoto
    // che resta, ed è dove la sezione torna.
    useLayoutEffect(() => {
        moveLibrary(showCentralLibrary ? stripBodyRef.current : libraryToolNames.includes(activeSceneEditorTool) ? railContentRef.current : null);
    }, [moveLibrary, showCentralLibrary, activeSceneEditorTool, libraryToolNames, isLibraryExpanded, preferences.collapsed]);

    // NEL SECONDO ATTRACCO LA COLONNA C'È SEMPRE (collega, 2026-09-09: «il pannello destro non
    // chiuderlo mai: sostituisci il contenuto interno»). Lì l'Inspector ha un rail suo —
    // «Selected», World, Effects — quindi è una PARETE della stanza, non il soggetto: farla sparire
    // a ogni clic a vuoto rifaceva la griglia due volte e si portava via anche il rail, cioè il
    // modo di tornare. Quando nessuno è scelto la colonna resta, e lo dice.
    //
    // NEL PRIMO ATTRACCO resta com'era: lì la colonna È il soggetto, rail non ne ha, e senza
    // soggetto occuperebbe spazio per dire «scegli qualcosa».
    const showAmbientInInspector = showCentralLibrary && inspectorTool === 'ambient';
    const showInspectorPanel = preferences.layoutMode === 'workspace' && (showCentralLibrary || Boolean(inspectorContent));
    const isInspectorCollapsed = isInspectorCollapsible && preferences.collapsed.includes('right');
    const isInspectorOpen = showInspectorPanel && !isInspectorCollapsed;

    // La griglia deve sapere *che cosa è davvero ridotto a rail*, non cosa risulta scritto
    // nelle preferenze: un «right» rimasto lì da quando la colonna si riduceva stringerebbe
    // una traccia che ora non ha niente da metterci dentro.
    const collapsedPanels = preferences.collapsed.filter((panel) => panel !== 'right' || isInspectorCollapsed).join(' ') || undefined;

    useLayoutEffect(() => {
        moveAmbient(
            showCentralLibrary
                ? isInspectorOpen && showAmbientInInspector
                    ? inspectorSlotRef.current
                    : null
                : ambientToolNames.includes(activeSceneEditorTool)
                  ? railContentRef.current
                  : null
        );
    }, [moveAmbient, showCentralLibrary, showAmbientInInspector, isInspectorOpen, activeSceneEditorTool, ambientToolNames]);

    // Scegliere una cosa riporta l'Inspector sulla cosa: è la risposta al gesto che hai
    // appena fatto, e trovarci ancora World sarebbe un silenzio.
    //
    // E RIAPRE LA COLONNA, SE ERA CHIUSA. La chiusura vive nelle preferenze e sopravvive al
    // ricaricamento: senza questo, un solo clic sulla ✕ rendeva muto ogni clic successivo su una
    // riga — il soggetto cambiava davvero, ma la colonna restava una striscia e non lo diceva
    // nessuno. Chi sceglie una cosa sta chiedendo di vederla, e la richiesta batte una chiusura
    // fatta ieri su un altro soggetto: a tenere la colonna stretta resta il gesto di chiuderla,
    // che è lì e si rifà in un clic.
    //
    // LO STATO LO LEGGE UN RIFERIMENTO, non le dipendenze: con `preferences.collapsed` fra le
    // dipendenze, chiudere la colonna di SINISTRA rieseguirebbe questo effetto e riporterebbe
    // l'Inspector su «Selected» mentre stai guardando World.
    const revealInspector = useRef<() => void>(() => {});
    revealInspector.current = () => {
        setInspectorTool('selected');
        if (preferences.collapsed.includes('right')) toggleCollapsed('right');
    };

    useEffect(() => {
        if (inspectorContent) revealInspector.current();
    }, [inspectorContent]);
    const workspaceStyle = {
        '--w-left': `${preferences.sizes.left}px`,
        '--w-right': `${preferences.sizes.right}px`,
        '--h-assets': showCentralLibrary ? `${isLibraryExpanded ? preferences.sizes.assets : 43}px` : '0px',
        '--w-rail-cur': `${getWorkspaceRailWidth(preferences.sectionRails)}px`
    } as React.CSSProperties;

    return (
        <Load3DSceneEditor onProgress={() => null} fallback={<OverlayLoading forceShow />} damMode={false} isConfiguratorProduct={true} notLoadDesign={true}>
            <Container>
                {/* Non disegna niente: dice al motore chi è il provider VTO di questo prodotto. */}
                <VTOProviderSync productModelID={zkProduct?.get?.('modelID')} />
                <HeaderToolbar
                    closeOverlay={closeOverlay}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onSave={handleSave}
                    isSaving={isSaving}
                    onSettings={openSettings}
                    activeMode='scene'
                />
                <MainContentContainer>
                    <Workspace
                        ref={workspaceRef}
                        onChangeCapture={() => markSceneDirty()}
                        onInputCapture={() => markSceneDirty()}
                        className='workspace'
                        data-layout={preferences.layoutMode}
                        data-dock={preferences.propertiesPanel}
                        data-inspector={showInspectorPanel ? 'on' : 'off'}
                        data-collapse={collapsedPanels}
                        style={workspaceStyle}
                    >
                        <Panel data-panel='left'>
                            <LeftVerticalToolbar
                                tools={railTools}
                                selectedTool={railSelectedTool}
                                /* CAMBIARE SEZIONE NON CHIUDE IL PANNELLO. La colonna di destra mostra il
                                   SOGGETTO, e cambiare la lista da cui si sceglie non è dire che non si sta
                                   più guardando niente: era un pannello che si chiudeva da solo mentre
                                   navighi, portandosi via quello che stavi tenendo d'occhio.

                                   E siccome la colonna esce dalla griglia quando non ha contenuto, quel
                                   vuoto non era nemmeno un pannello vuoto: era la colonna che spariva e
                                   ricompariva, spostando tutto il resto due volte.

                                   Il soggetto lo cambia chi ne sceglie un altro, o chi chiude. */
                                onSelectTool={setActiveTool}
                                onClosePanel={() => toggleCollapsed('left')}
                                collapsed={preferences.collapsed.includes('left')}
                                sectionRails={preferences.sectionRails}
                                onInspectorChange={setInspectorContent}
                                hostedTools={relocatedToolNames}
                                contentRef={railContentRef}
                            />
                        </Panel>
                        {/* LA PRESA STA FUORI DAL PANNELLO, non dentro. Dentro veniva tagliata dal suo
                            `overflow: hidden` e si misurava sul workspace invece che sul pannello: c'era,
                            ma non si poteva né vedere né afferrare. Da qui si posiziona da sé sulla
                            cucitura fra la prima colonna e ciò che le sta accanto.
                            E non c'è quando il pannello è chiuso: un confine si tira solo se ha due lati
                            da spartire. */}
                        {!preferences.collapsed.includes('left') && (
                            <Resizer
                                direction='left'
                                value={preferences.sizes.left}
                                min={leftMinimum}
                                max={WORKSPACE_MAX_PANEL_SIZE}
                                defaultValue={332}
                                workspaceRef={workspaceRef}
                                cssVariable='--w-left'
                                onCommit={(value) => setPanelSize('left', value)}
                            />
                        )}
                        <StageColumn ref={stageRef} data-panel='stage' $hasAssetBar={showCentralLibrary}>
                            <PreviewContainer $previewMode={previewMode}>
                                <SmallScreenBanner>
                                    <NotificationBanner type='INFO' variant='slim' content={T._('The side panels are hidden on small screens.', 'Admin')} />
                                </SmallScreenBanner>
                                {zkScene && zkProduct && sceneEditor && (
                                    <>
                                        <SceneEditorView
                                            variantId={zkProduct.get('colors').at(0).get('colorID')}
                                            isHighlightActive={true}
                                            selectedMeshId={null}
                                            isSelectionMode={true}
                                            onSelectionChanged={handleMeshSelected}
                                            onMeshesUpdated={handleMeshesUpdated}
                                            onMaterialsUpdated={handleMaterialsUpdated}
                                            onLightsUpdated={handleLightsUpdated}
                                            onCameraLocationsUpdated={handleCameraLocationsUpdated}
                                            onSceneTasksCheck={handleSceneTasksCheck}
                                        />
                                        {isSceneEditorReady && (
                                            <SceneEditorToolbar
                                                sceneEditor={sceneEditor}
                                                stageRef={stageRef}
                                                onPreviewModeChange={setPreviewMode}
                                                measurementUnit={preferences.measurementUnit}
                                                onMeasurementUnitChange={(unit) => setPreference('measurementUnit', unit)}
                                            />
                                        )}
                                    </>
                                )}
                                {!sceneEditor && <StageEmptyState>{T._('Loading scene...', 'Admin')}</StageEmptyState>}
                            </PreviewContainer>
                            {showCentralLibrary && (
                                <AssetBar data-panel='assets' aria-label={T._('Asset library', 'Admin')}>
                                    <SceneEditorLibraryDrawer
                                        categories={libraryCategories}
                                        activeTool={activeLibraryTool}
                                        onSelectTool={setLibraryTool}
                                        isOpen={isLibraryExpanded}
                                        onToggle={() => {
                                            if (preferences.collapsed.includes('assets')) {
                                                toggleCollapsed('assets');
                                            } else {
                                                setLibraryOpen((current) => !current);
                                            }
                                        }}
                                        bodyRef={stripBodyRef}
                                    />
                                </AssetBar>
                            )}
                            {/* Il manico del divisore vive dove vive la colonna che allarga: senza
                                Inspector aperto tirerebbe una traccia che non c'è. */}
                            {preferences.propertiesPanel === 'right' && isInspectorOpen && (
                                <Resizer
                                    direction='right'
                                    value={preferences.sizes.right}
                                    min={rightMinimum}
                                    max={WORKSPACE_MAX_PANEL_SIZE}
                                    defaultValue={332}
                                    workspaceRef={workspaceRef}
                                    referenceRef={stageRef}
                                    cssVariable='--w-right'
                                    onCommit={(value) => setPanelSize('right', value)}
                                />
                            )}
                            {showCentralLibrary && isLibraryExpanded && (
                                <Resizer
                                    direction='assets'
                                    value={preferences.sizes.assets}
                                    min={assetsMinimum}
                                    max={WORKSPACE_MAX_PANEL_SIZE}
                                    defaultValue={284}
                                    workspaceRef={workspaceRef}
                                    cssVariable='--h-assets'
                                    onCommit={(value) => setPanelSize('assets', value)}
                                />
                            )}
                        </StageColumn>
                        {/* ANCHE IL SECONDO PANNELLO SI ALLARGA. Il primo aveva la sua presa dal principio, il
                            secondo no: nell'attracco `right` la cucitura è fra palco e Inspector e la presa vive
                            là, ma qui l'Inspector sta IN MEZZO — fra le sezioni e il palco — e quella cucitura non
                            aveva nessuno. Due colonne larghe uguali e nessun modo di dare aria a quella che in quel
                            momento stai leggendo. */}
                        {preferences.propertiesPanel === 'side' && isInspectorOpen && (
                            <Resizer
                                direction='side'
                                value={preferences.sizes.right}
                                min={rightMinimum}
                                max={WORKSPACE_MAX_PANEL_SIZE}
                                defaultValue={332}
                                workspaceRef={workspaceRef}
                                referenceRef={inspectorRef}
                                cssVariable='--w-right'
                                onCommit={(value) => setPanelSize('right', value)}
                            />
                        )}
                        {showInspectorPanel && (
                            <InspectorPanel ref={inspectorRef} data-panel='right' $collapsed={isInspectorCollapsed}>
                                {isInspectorCollapsed ? (
                                    <InspectorToggleButton
                                        type='button'
                                        $rotate={90}
                                        onClick={() => toggleCollapsed('right')}
                                        aria-label={T._('Open inspector', 'Admin')}
                                        title={T._('Open inspector', 'Admin')}
                                    >
                                        <ChevronIcon />
                                    </InspectorToggleButton>
                                ) : (
                                    // La testata è del SOGGETTO, non della colonna: la disegna
                                    // `InspectorShell` insieme a nome e specifiche. Qui resta
                                    // solo il comando che appartiene alla colonna — chiuderla —
                                    // che il guscio va a prendere da questo contesto.
                                    <InspectorChromeProvider value={inspectorChrome}>
                                        {showCentralLibrary && (
                                            <InspectorRail role='tablist' aria-label={T._('Inspector sections', 'Admin')}>
                                                <InspectorRailItem
                                                    type='button'
                                                    role='tab'
                                                    $active={inspectorTool === 'selected'}
                                                    aria-selected={inspectorTool === 'selected'}
                                                    onClick={() => setInspectorTool('selected')}
                                                >
                                                    <SelectedIcon aria-hidden='true' />
                                                    <span>{T._('Selected', 'Admin')}</span>
                                                </InspectorRailItem>
                                                {ambientToolNames.map((tool) => (
                                                    <InspectorRailItem
                                                        key={tool}
                                                        type='button'
                                                        role='tab'
                                                        $active={inspectorTool === 'ambient' && activeAmbientTool === tool}
                                                        aria-selected={inspectorTool === 'ambient' && activeAmbientTool === tool}
                                                        onClick={() => {
                                                            setAmbientTool(tool);
                                                            setInspectorTool('ambient');
                                                        }}
                                                    >
                                                        {sceneEditorTools.find((stepTool) => stepTool.tool === tool)?.icon}
                                                        <span>{tool}</span>
                                                    </InspectorRailItem>
                                                ))}
                                            </InspectorRail>
                                        )}
                                        <InspectorContent>
                                            {showAmbientInInspector ? (
                                                <InspectorSectionSlot ref={inspectorSlotRef} />
                                            ) : (
                                                (inspectorContent ?? (
                                                    <InspectorEmpty>
                                                        <SelectedIcon aria-hidden='true' />
                                                        <p>{T._('Select something in a list or in the model to see its properties.', 'Admin')}</p>
                                                    </InspectorEmpty>
                                                ))
                                            )}
                                        </InspectorContent>
                                    </InspectorChromeProvider>
                                )}
                            </InspectorPanel>
                        )}
                    </Workspace>
                    {/* Le sezioni che cambiano casa vivono qui, ferme nell'albero di React, e si
                        vedono dentro il nodo che trasloca. `SectionPlacementProvider` è l'unica
                        cosa che deve sapere di dove si trovano: la ricerca sale sulla riga dei
                        comandi quando è nella striscia, che è bassa e larga. */}
                    {ambientHostRef.current &&
                        ambientSection &&
                        createPortal(<ambientSection.component onInspectorChange={setInspectorContent} />, ambientHostRef.current)}
                    {libraryHostRef.current &&
                        librarySection &&
                        createPortal(
                            <SectionPlacementProvider value={showCentralLibrary ? 'strip' : 'panel'}>
                                <librarySection.component onInspectorChange={setInspectorContent} />
                            </SectionPlacementProvider>,
                            libraryHostRef.current
                        )}
                </MainContentContainer>
            </Container>
        </Load3DSceneEditor>
    );
};

export default SceneEditor;
